import { Objective, KeyResult } from "@/types";
import { ObjectiveCard } from "./ObjectiveCard";
import { KeyResultList } from "./KeyResultList";
import { Skeleton } from "@/components/ui/skeleton";
import { Target, AlertCircle } from "lucide-react";
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { OBJECTIVE_DROPPABLE, TREE_ROW_COLS } from "./okr-tree-constants";

interface ObjectiveListProps {
  objectives: Objective[];
  isLoading: boolean;
  isError: boolean;
  expandedPids: Set<string>;
  onToggleExpand: (pid: string) => void;
  onViewObjective: (objective: Objective) => void;
  onEditObjective: (objective: Objective) => void;
  onDeleteObjective: (objective: Objective) => void;
  onAddKeyResult: (objective: Objective) => void;
  onEditKeyResult: (keyResult: KeyResult) => void;
  onDeleteKeyResult: (keyResult: KeyResult) => void;
  /** Single handler for both levels; dispatch on `result.type` ("OBJECTIVE" | "KR"). */
  onDragEnd?: (result: DropResult) => void;
  isDragEnabled?: boolean;
  isKrDragEnabled?: boolean;
  canManage?: boolean;
  hasFilters?: boolean;
}

const HEADER_CELL = "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground";

export function ObjectiveList({
  objectives,
  isLoading,
  isError,
  expandedPids,
  onToggleExpand,
  onViewObjective,
  onEditObjective,
  onDeleteObjective,
  onAddKeyResult,
  onEditKeyResult,
  onDeleteKeyResult,
  onDragEnd,
  isDragEnabled = false,
  isKrDragEnabled = false,
  canManage = false,
  hasFilters = false,
}: ObjectiveListProps) {
  const dragObjectives = isDragEnabled && !!onDragEnd;
  const dragKrs = isKrDragEnabled && !!onDragEnd;

  const header = (
    <div className="grid gap-3 px-6 h-11 items-center bg-muted/40 border-b" style={TREE_ROW_COLS}>
      <div className={HEADER_CELL}>Objective / KR / PPP</div>
      <div className={HEADER_CELL}>Status</div>
      <div className={HEADER_CELL}>Progress</div>
      <div className={HEADER_CELL}>Owner / DRI</div>
      <div />
    </div>
  );

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-background overflow-hidden">
        {header}
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="grid gap-3 px-6 min-h-[52px] items-center border-b border-border/40" style={TREE_ROW_COLS}>
            <div className="flex items-center gap-2 pl-[46px]"><Skeleton className="h-3 w-56" /></div>
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-1 w-full rounded-full" />
            <Skeleton className="h-4 w-24" />
            <div />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="h-12 w-12 text-destructive/50 mb-4" />
        <h3 className="text-lg font-semibold">Failed to load objectives</h3>
        <p className="text-sm text-muted-foreground mt-1">Please try again later.</p>
      </div>
    );
  }

  if (objectives.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/50 py-16 text-center">
        <Target className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold">No objectives found</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {hasFilters
            ? "No objectives match your current filters. Try adjusting your search or filters."
            : "Create your first objective to start tracking OKRs."}
        </p>
      </div>
    );
  }

  const renderRow = (objective: Objective, dragHandleProps: DraggableProvidedDragHandleProps | null) => (
    <>
      <ObjectiveCard
        objective={objective}
        onView={onViewObjective}
        onEdit={onEditObjective}
        onDelete={onDeleteObjective}
        onAddKeyResult={onAddKeyResult}
        expanded={expandedPids.has(objective.pid)}
        onToggleExpand={() => onToggleExpand(objective.pid)}
        dragHandleProps={dragHandleProps}
        canManage={canManage}
      />
      {expandedPids.has(objective.pid) && (
        <KeyResultList
          objectivePid={objective.pid}
          onEditKeyResult={onEditKeyResult}
          onDeleteKeyResult={onDeleteKeyResult}
          isDragEnabled={dragKrs}
          canManage={canManage}
        />
      )}
    </>
  );

  const rows = dragObjectives
    ? objectives.map((objective, index) => (
        <Draggable key={objective.pid} draggableId={objective.pid} index={index}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.draggableProps}>
              {renderRow(objective, provided.dragHandleProps)}
            </div>
          )}
        </Draggable>
      ))
    : objectives.map((objective) => <div key={objective.pid}>{renderRow(objective, null)}</div>);

  const body = dragObjectives ? (
    <Droppable droppableId={OBJECTIVE_DROPPABLE} type="OBJECTIVE">
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          {rows}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  ) : (
    <div>{rows}</div>
  );

  return (
    <div className="rounded-xl border bg-background overflow-hidden">
      {header}
      {dragObjectives || dragKrs ? (
        <DragDropContext onDragEnd={onDragEnd!}>{body}</DragDropContext>
      ) : (
        body
      )}
    </div>
  );
}
